import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CalendarDays, FileCheck2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { bookingService } from "@/services/bookingService";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { notificationService } from "@/services/notificationService";
import { formatDateTime } from "@/utils/format";
import { tx } from "@/i18n/tx";

const POLL_MS = 30000;
const SEEN_KEY = "arcquan.notif.seen";

function playPling() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.type = "sine"; osc.frequency.setValueAtTime(880, ctx.currentTime); osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime); gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    osc.connect(gain).connect(ctx.destination); osc.start(); osc.stop(ctx.currentTime + 0.5);
    setTimeout(() => ctx.close(), 700);
  } catch { /* audio blocked until user interaction – ignore */ }
}

function desktopNotify(n) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    const d = new Notification(n.title || "Ny notis", { body: n.body || "", tag: n.id, icon: "/brand/logo.png" });
    d.onclick = () => { window.focus(); if (n.booking_id) window.location.assign(`/dashboard/bookings?open=${n.booking_id}`); d.close(); };
  } catch { /* ignore */ }
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { ctx } = useAuth();
  const canConfirm = (ctx?.permissions || []).includes("bookings.update");
  const [items, setItems] = useState([]);
  const [confirming, setConfirming] = useState("");
  const seenRef = useRef(null);

  const load = useCallback(() => {
    notificationService.list().then((d) => {
      const list = Array.isArray(d) ? d : [];
      const seen = seenRef.current ?? new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]"));
      const fresh = seenRef.current ? list.filter((n) => !n.read && !seen.has(n.id)) : [];
      if (fresh.length) { playPling(); fresh.slice(0, 3).forEach(desktopNotify); }
      list.forEach((n) => seen.add(n.id));
      seenRef.current = seen;
      sessionStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-300)));
      setItems(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const unread = items.filter((n) => !n.read).length;

  const open = async (n) => {
    if (!n.read) {
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      notificationService.markRead(n.id).catch(() => {});
    }
    if (n.booking_id) navigate(`/dashboard/bookings?open=${n.booking_id}`);
    else if (n.workorder_id) navigate(`/dashboard/workorders?open=${n.workorder_id}`);
  };

  const confirm = async (e, n) => {
    e.stopPropagation(); e.preventDefault();
    setConfirming(n.id);
    try {
      await bookingService.updateStatus(n.booking_id, "Confirmed", "Bekräftad från notis");
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true, confirmed: true } : x)));
      notificationService.markRead(n.id).catch(() => {});
      toast.success(`Bokning ${n.booking_number || ""} bekräftad – kunden har mejlats.`);
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || "Kunde inte bekräfta bokningen.");
    } finally { setConfirming(""); }
  };

  return (
    <DropdownMenu onOpenChange={(o) => { if (o) { load(); if (typeof Notification !== "undefined" && Notification.permission === "default") Notification.requestPermission().catch(() => {}); } }}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-lg text-muted-foreground hover:text-foreground"
          data-testid="notification-bell" aria-label={`Notiser${unread ? ` (${unread} olästa)` : ""}`}>
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notification-badge"
              className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1 text-[11px] font-bold text-primary-foreground ring-2 ring-background">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-popover p-0" data-testid="notification-list">
        <DropdownMenuLabel className="flex items-center justify-between px-3 py-2.5">
          <span>Notiser</span>
          {unread > 0 && <span className="text-xs font-normal text-muted-foreground">{unread} olästa</span>}
        </DropdownMenuLabel>
        {typeof Notification !== "undefined" && Notification.permission === "denied" && (
          <p className="px-3 pb-2 text-[11px] text-muted-foreground">{tx("Webbläsarnotiser är blockerade – tillåt dem i adressfältet för att få pling på skrivbordet.")}</p>
        )}
        <DropdownMenuSeparator className="my-0" />
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="notification-empty">{tx("Inga notiser ännu.")}</p>}
          {items.slice(0, 30).map((n) => {
            const Icon = n.type === "booking_created" ? CalendarDays : FileCheck2;
            return (
              <DropdownMenuItem key={n.id} onClick={() => open(n)} data-testid="notification-item"
                className={`flex cursor-pointer items-start gap-3 rounded-none px-3 py-3 ${n.read ? "opacity-70" : "bg-primary/5"}`}>
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${n.read ? "bg-muted text-muted-foreground" : "bg-primary/15 text-primary"}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold">{n.title}</span>
                    {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                  </span>
                  <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{n.body}</span>
                  <span className="mt-1 block text-[11px] text-muted-foreground/70">{formatDateTime(n.created_at)}</span>
                  {n.type === "booking_created" && n.booking_id && canConfirm && (
                    <span className="mt-2 block">
                      {n.confirmed ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600" data-testid="notification-confirmed"><Check className="h-3.5 w-3.5" /> {tx("Bekräftad")}</span>
                      ) : (
                        <button type="button" onClick={(e) => confirm(e, n)} disabled={confirming === n.id} data-testid="notification-confirm-button"
                          className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                          {confirming === n.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Bekräfta
                        </button>
                      )}
                    </span>
                  )}
                </span>
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
